/**
 * Protected Route Component
 * =========================
 * Wraps routes that require authentication and/or specific roles
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';

/**
 * ProtectedRoute Component
 * 
 * @param {Object} props
 * @param {React.Component} props.children - The component to render if authorized
 * @param {string|string[]} props.requiredRole - Required role(s) to access this route
 * @param {string} props.redirectTo - Where to redirect if unauthorized (default: /login)
 */
export const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  redirectTo = '/login' 
}) => {
  const { user, role, loading } = useAuth();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role requirement if specified
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    if (!allowedRoles.includes(role)) {
      // User doesn't have required role - redirect based on their actual role
      switch (role) {
        case 'admin':
          return <Navigate to="/admin" replace />;
        case 'vendor':
          return <Navigate to="/vendor-portal" replace />;
        case 'customer':
          return <Navigate to="/customer-portal" replace />;
        default:
          return <Navigate to="/" replace />;
      }
    }
  }

  // All checks passed - render the protected component
  return children;
};

export default ProtectedRoute;

